import { CncReportLocal } from '@gameonly/core';
import { Alert, Box, DialogActions, TextField } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import Button, { WhiteButton } from '../ui/Button';

interface CreateLocalPartnerProps {
  onClose: () => void;
  onSave: (local: CncReportLocal) => void;
}

interface PartnerValues {
  studio: string;
  file: any;
}

const initialValues: PartnerValues = {
  studio: '',
  file: null,
};

const getValidationSchema = (t: (key: string) => string) => {
  return Yup.object().shape({
    studio: Yup.string()
      .required(t('cnc-reports:create-modal-partner.studio-required'))
      .min(3, t('cnc-reports:create-modal-partner.studio-min')),
    file: Yup.mixed().required(
      t('cnc-reports:create-modal-partner.file-required'),
    ),
  });
};

const validateJsonFile = async (file: File): Promise<any> => {
  if (!file) throw new Error('File is null');
  if (file.type !== 'application/json')
    throw new Error('File must be a JSON file');
  const content = await readFileContent(file);
  return content;
};

const readFileContent = (file: File): Promise<any> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        resolve(JSON.parse(reader.result as string));
      } catch (err) {
        reject(new Error('Invalid JSON content'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsText(file);
  });
};

const getPartnerLocal = (values: PartnerValues): CncReportLocal => {
  return {
    id: values.file.projectId,
    type: 'partner',
    validated: true,
    name: values.file.name,
    data: { ...values.file, studio: values.studio },
  };
};

const CreateLocalPartner = ({ onClose, onSave }: CreateLocalPartnerProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { t } = useTranslation(['cnc-reports', 'common']);

  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void,
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      try {
        const { studio, ...content } = await validateJsonFile(file);
        setFieldValue('file', content);
        setFieldValue('studio', studio);
        setErrorMessage(null);
      } catch (error: any) {
        setErrorMessage(error.message);
      }
    }
  };

  const handleValidate = (values: PartnerValues) => {
    try {
      if (!values.file)
        throw new Error('cnc-reports:create-modal-partner.file-required');
      const local = getPartnerLocal(values);
      onSave(local);
      onClose();
    } catch (error: any) {
      setErrorMessage(
        error.message || 'cnc-reports:create-modal-partner.generic-error',
      );
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getValidationSchema(t)}
      onSubmit={handleValidate}
    >
      {({ values, setFieldValue, errors, touched }) => {
        return (
          <Form>
            {errorMessage && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {errorMessage}
              </Alert>
            )}
            <Button component="label" sx={{ mt: 2 }}>
              {t('cnc-reports:create-modal-partner.upload-JSON')}
              <input
                type="file"
                accept="application/json"
                hidden
                onChange={(e) => handleChange(e, setFieldValue)}
              />
            </Button>
            {values.file && !errorMessage && (
              <>
                {' '}
                <Box mt={1}>
                  {t('cnc-reports:create-modal-partner.upload-JSON-successful')}
                </Box>
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="studio"
                  label={t('cnc-reports:create-modal-partner.studio')}
                  error={touched.studio && Boolean(errors.studio)}
                  helperText={touched.studio && errors.studio}
                />
              </>
            )}

            <DialogActions>
              <WhiteButton onClick={onClose}>{t('common:cancel')}</WhiteButton>
              <Button type="submit" color="primary">
                {t('common:validate')}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateLocalPartner;
